import React, { FC, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import useFlash from "go-alert/AlertMessage";
import { mapCustomFieldsToForm } from "go-app/components/CustomFields";
import CustomFieldsForm from "go-app/components/CustomFields/CustomFieldsForm";
import handleError from "go-app/services/errors";
import handleException from "go-core/api/handleException";
import { ApiError } from "go-core/api/types";
import { formatDateToString } from "go-core/components/Formatters/FormatDate";
import { LoadingContainer } from "go-core/components/Loading";
import { FormDirty } from "go-form/components/FormDirty";
import { useCustomErrors } from "go-form/hooks";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { CustomFieldTemplateApi } from "go-segment/services/types";
import AttachmentsForm from "../../../../../../../../../../components/Attachment/AttachmentsForm";
import {
	TagsFormInput,
	mapTagsToAcceptableByBackendFormat,
} from "../../../../../../../../../../components/Tag/TagsFormInput";
import {
	PurchaseApi,
	PurchaseItemApi,
	StockApi,
	TaxApi,
} from "../../../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../../../services/Api/api";
import ItemsError from "../../../../components/ItemsError";
import { useExternalProductsState } from "../../../../hooks/useExternalProductsState";
import PurchaseBasicInfoForm from "./PurchaseBasicInfoForm";
import PurchaseFormItemsTable from "./PurchaseFormItemsTable/FormItemsTable/PurchaseFormItemsTable";

interface Props {
	purchase?: PurchaseApi;
	customFieldsConfig?: CustomFieldTemplateApi[];
	fetchPurchase?: () => void;
}

const PurchaseForm: FC<Props> = ({ purchase, customFieldsConfig, fetchPurchase }) => {
	const { t } = useTranslation();
	const organization = useSelector(selectOrganization);
	const { addFlash, addSuccessFlash } = useFlash();
	const history = useHistory();
	const form = useForm<PurchaseApi>({
		criteriaMode: "all",
		defaultValues: {
			...purchase,
			currency: purchase?.currency || organization?.currency,
			purchased_at: purchase?.purchased_at || formatDateToString(new Date()),
			tax_gross: purchase?.tax_gross !== undefined ? purchase.tax_gross : false,
			items: !purchase?.items
				? [{ position: 0 } as PurchaseItemApi]
				: [...purchase.items, { position: purchase.items.length }],
			custom_fields: mapCustomFieldsToForm(customFieldsConfig || [], purchase?.custom_fields || []),
		},
	});

	const { formState, setError, handleSubmit, setValue, watch, reset } = form;
	const [loading, setLoading] = useState<boolean>(false);
	const [taxes, setTaxes] = useState<TaxApi[] | undefined>(undefined);
	const [formErrors, setFormErrors] = useState<ApiError[]>([]);
	const { getExternalStates, selectedContact, externalProductStates, getExternalStateName } =
		useExternalProductsState(purchase);

	useEffect(() => {
		onSetStockOnFirstRender();
	}, []);

	useEffect(() => {
		onFetchTaxes();
	}, []);

	const { setErrors, validateCustomErrors } = useCustomErrors(setError);

	const onSetStockOnFirstRender = async () => {
		const stocks = await onSearchStocks("", {});
		if (stocks.length === 1 && !watch("stock_id")) {
			const newStock = stocks[0];
			setValue("stock_id", Number(newStock.id));
			setValue("stock", {
				id: newStock.id,
				name: newStock.label,
			} as StockApi);
		}
	};

	const onFetchTaxes = async () => {
		try {
			const res = await api.organization().getTaxes();
			setTaxes(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const parseFormData = (data: PurchaseApi) => {
		data.items = [
			...data.items.filter(
				(item) =>
					item?.volume ||
					item?.product?.name ||
					item?.quantity ||
					item?.tax_id ||
					item?.price_net_money?.amount ||
					item?.total_price_net_money?.amount
			),
		];
		if (data.tags) data.tags = mapTagsToAcceptableByBackendFormat(data.tags);

		return data;
	};

	const onSearchStocks = async (t: string, obj: Record<string, any>, options?: Record<string, any>) => {
		return api.organization().getStockSearchSelect(t, obj, {
			cancelToken: options?.token,
		});
	};

	const onSubmit = handleSubmit(async (data: PurchaseApi) => {
		if (!validateCustomErrors()) {
			return;
		}

		setLoading(true);
		let res;
		data = parseFormData(data);

		try {
			if (purchase?.id) {
				const params = { include: "all" };
				res = await api.organization().updatePurchase(data, params);
				reset({
					...res,
					items: [...res.items, { position: res.items.length } as PurchaseItemApi],
					custom_fields: mapCustomFieldsToForm(customFieldsConfig || [], res?.custom_fields || []),
				});
			} else {
				res = await api.organization().createPurchase(data);
			}
			addSuccessFlash(t("common.flash.saved", { ns: "lib" }));
			history.push(`/${organization.id}/documents/purchases/${res.id}`);
		} catch (err) {
			handleError.form(err, setError, addFlash, ["items"]);
			const exceptionErrors = handleException(err);
			setFormErrors([...exceptionErrors]);
		}
		setLoading(false);
	});

	if (!taxes) return <LoadingContainer />;

	return (
		<FormDirty
			formState={formState}
			key="purchase-form"
			onSubmit={(e) => {
				onSubmit(e);
			}}
			loading={loading}
			className="purchase-form"
		>
			<h5>{t("common.word.basic_info")}</h5>
			<PurchaseBasicInfoForm
				form={form}
				purchase={purchase}
				setErrors={setErrors}
				getExternalStates={getExternalStates}
				selectedContact={selectedContact}
			/>
			{customFieldsConfig && customFieldsConfig.length > 0 && (
				<fieldset className="form-group">
					<h5>{t("common.word.custom_fields")}</h5>
					<CustomFieldsForm
						customFieldsConfig={customFieldsConfig}
						customFields={purchase?.custom_fields || []}
						form={form}
						setErrors={setErrors}
					/>
				</fieldset>
			)}
			<ItemsError
				apiErrors={formErrors}
				formErrors={formState.errors}
				register={form.register}
				watch={form.watch}
				setErrors={setErrors}
			/>
			<PurchaseFormItemsTable
				form={form}
				taxes={taxes}
				purchase={purchase}
				fetchPurchase={fetchPurchase}
				getExternalStates={getExternalStates}
				externalProductStates={externalProductStates}
				getExternalStateName={getExternalStateName}
			/>
			<h5>{t("lib:common.word.advanced")}</h5>
			<div>
				<TagsFormInput form={form} />
				<span className="mt-4 d-block">{t("common.word.attachments")}</span>
				<AttachmentsForm form={form} />
			</div>
		</FormDirty>
	);
};

export default PurchaseForm;
